import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Loader from '../../component/common/Loader.js';
import { HealthProvider, useHealth } from '../../context/HealthContext.js';
import { HealthLoadingProvider, useHealthLoading } from '../../context/HealthLoadingContext.js';
import healthRoutes from '../../routes/healthRoute.js';
import Topbar from '../PulseSurvey/Topbar.js';
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Error boundary for catching undefined components
class HealthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    console.error('Health component error:', error);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Health component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <h2 className="text-lg font-medium text-red-600">Something went wrong.</h2>
          <p className="text-gray-500 mt-2">Please refresh the page or contact support.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

HealthErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

// Inner component that can access the context
const ShoorahHealthContent = () => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const { loading } = useHealth(); // Get loading state from context
  const { isLoading: healthLoading } = useHealthLoading(); // Get health-specific loading state

  const userData = JSON.parse(localStorage.getItem('userData'));
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown when route changes
  useEffect(() => {
    setActiveDropdown(null);
  }, [location.pathname]);

  return (
    <div className="min-h-screen ">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Shoorah Health</title>
      </Helmet>
      <div className="ml-2">
        <div className="flex justify-between items-center py-4">
          <Topbar title={'Shoorah Health'} />
        </div>
        {/* Main Content Area for Child Routes */}
        <main className="flex-1">
          <div className="">
            {(loading || healthLoading) && <Loader />}
            <Routes>
              {/* Default redirect when accessing /health */}
              <Route index element={<Navigate to="health-dashboard" replace />} />
              {healthRoutes.map((route, index) => {
                // Defensive check to ensure component exists
                if (!route.component) {
                  console.error(`Route component is undefined for path: ${route.path}`);
                  return null;
                }
                return <Route key={index} path={route.path} element={<route.component />} />;
              })}
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
}; // Main component that wraps content with provider
const ShoorahHealth = () => {
  return (
    <HealthErrorBoundary>
      <HealthProvider>
        <HealthLoadingProvider>
          <ShoorahHealthContent />
        </HealthLoadingProvider>
      </HealthProvider>
    </HealthErrorBoundary>
  );
};

ShoorahHealth.propTypes = {};

export default ShoorahHealth;
